import { create } from "zustand";

export const SKETCHUP_EXTENSION_WAREHOUSE_URL =
  "https://extensions.sketchup.com/extension/df0c4af4-301c-41fc-b566-a787a20c6894/matto-board-visual-curation-studio-and-interior-material-library";

export const sketchupPromoLocalKey = (uid: string) =>
  `sketchupPromoDismissed:${uid}`;

export const readLocalDismissed = (uid: string | undefined) => {
  if (!uid) return false;
  try {
    return !!localStorage.getItem(sketchupPromoLocalKey(uid));
  } catch {
    return false;
  }
};

type SketchupPromoProfile = {
  signupSurface?: string;
  sketchupFirstSeenAt?: unknown;
  sketchupBannerDismissedAt?: unknown;
};

/** Anyone who already uses the extension (signed up in the panel, or the
 *  backend stamped sketchupFirstSeenAt on a panel sign-in) is out of the
 *  promo audience — both the banner and the sidebar card skip them. */
function isSketchupPromoAudience(profile: SketchupPromoProfile): boolean {
  if (profile.signupSurface === "sketchup") return false;
  return !profile.sketchupFirstSeenAt;
}

/** The /home/library SketchUp promo: desktop only, promo audience only, and
 *  hidden for good once dismissed. */
export function shouldShowSketchupPromo({
  profile,
  localDismissed,
  isMobile,
}: {
  profile: SketchupPromoProfile | null;
  localDismissed: boolean;
  isMobile: boolean;
}): boolean {
  if (isMobile || !profile || localDismissed) return false;
  if (!isSketchupPromoAudience(profile)) return false;
  return !profile.sketchupBannerDismissedAt;
}

/**
 * The permanent sidebar card: same audience as the banner, but it only
 * appears once the banner has been dismissed (profile stamp, localStorage,
 * or this session's click) and then stays. Desktop-only placement is the
 * sidebar's job (the card never mounts on the mobile drawer).
 */
export function shouldShowSketchupCard({
  profile,
  bannerDismissed,
}: {
  profile: SketchupPromoProfile | null;
  /** localStorage flag or the in-session dismissal. */
  bannerDismissed: boolean;
}): boolean {
  if (!profile || !isSketchupPromoAudience(profile)) return false;
  return bannerDismissed || !!profile.sketchupBannerDismissedAt;
}

/**
 * In-session promo state. The profile stamp is an async write with retries
 * and a localStorage write re-renders nothing (or fails when storage is
 * blocked), so the banner and the card both read the dismissal from here.
 * Keyed by uid, not a boolean, so a same-tab account switch inherits nothing.
 */
export const useSketchupPromoStore = create<{
  /** uid whose library banner was closed this session. */
  dismissedUid: string | null;
  /** uid the sidebar card's Viewed event was sent for: once per session,
   *  not per mount (the card remounts on every rail collapse/route change). */
  cardViewedUid: string | null;
  markBannerDismissed: (uid: string) => void;
  markCardViewed: (uid: string) => void;
}>((set) => ({
  dismissedUid: null,
  cardViewedUid: null,
  markBannerDismissed: (uid) => set({ dismissedUid: uid }),
  markCardViewed: (uid) => set({ cardViewedUid: uid }),
}));
